import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { Form, Row, Col, Input, Select, Space, Button, Table, Tooltip, message, Cascader, Typography, Popover, Tag, Card, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import AddOrgDrawer from '../components/AddOrgDrawer';
import ViewOrgDrawer from '../components/ViewOrgDrawer';
import copy from 'copy-to-clipboard';
import './index.less';
import { orgTypeList } from '@/utils/constants';
import { regionOptions } from '@/utils/region';
import { mergeListApi } from '@/services/DC/Organization';
import { organizationSliceActions } from '../store/organizationSlice';
import { useDispatch, useSelector } from 'react-redux';
import config from '@config';
import { prepareBCOSSUrl } from '@/utils/utils';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
const { Text, Link } = Typography;

const { setOrganizationMergeFilter: setFilter, setOrganizationMergeCursor: setCursor } = organizationSliceActions;

const OrganizationMergeList = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { token } = theme.useToken();

    const dispatch = useDispatch();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const filter = useSelector(state => state.organization?.organizationMerge?.filter || { page: 1, size: 20 });
    const cursor = useSelector(state => state.organization?.organizationMerge?.cursor || 0);

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [openAddOrgDrawer, setOpenAddOrgDrawer] = useState(false); // 新增drawer
    const [openViewOrgDrawer, setOpenViewOrgDrawer] = useState(false); // 查看drawer
    const [currentRecordId, setCurrentRecordId] = useState(undefined);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 查看
    const handleView = record => {
        setOpenViewOrgDrawer(true);
        setCurrentRecordId(record.id);
        dispatch(setCursor(record.id));
    };

    // 准备医疗机构级别和等级信息
    // - 级别为三级、二级、一级时，显示级别；等次为甲等、乙等、丙等时，显示等次
    // - 级别和等次均显示时，简化显示：三级甲等 -> 三甲，二级甲等 -> 二甲，一级甲等 -> 一甲
    // - 级别和等次有其一时，不简化
    const prepareMedLevelGrade = unit => {
        let levelWithGrade = '';
        const level = unit?.medLevel;
        const grade = unit?.medGrade;

        // 判断是否有有效的级别和等次
        const hasValidLevel = level && (level === '三级' || level === '二级' || level === '一级');
        const hasValidGrade = grade && (grade === '甲等' || grade === '乙等' || grade === '丙等');

        if (hasValidLevel && hasValidGrade) {
            // 级别和等次均有时，简化显示：三级甲等 -> 三甲
            if (grade === '甲等') {
                levelWithGrade = `${level.replace('级', '')}甲`;
            } else if (grade === '乙等') {
                levelWithGrade = `${level.replace('级', '')}乙`;
            } else if (grade === '丙等') {
                levelWithGrade = `${level.replace('级', '')}丙`;
            }
        } else if (hasValidLevel) {
            // 只有级别时，不简化，直接显示级别
            levelWithGrade = level;
        } else if (hasValidGrade) {
            // 只有等次时，也不简化，直接显示等次
            levelWithGrade = grade;
        }

        return levelWithGrade;
    };

    const onFinish = values => {
        const newValues = { ...values };
        newValues.orgType = newValues.orgType && newValues.orgType.length ? newValues.orgType : undefined;
        // 所在地区 - 拆分保存
        if (values.areas && values.areas.length > 0) {
            newValues.provinceCode = values.areas[0];
            newValues.cityCode = values.areas[1];
            newValues.districtCode = values.areas[2];
        } else {
            newValues.provinceCode = undefined;
            newValues.cityCode = undefined;
            newValues.districtCode = undefined;
        }
        // 机构标识
        if (values.keywordList) {
            newValues.keywordList = values.keywordList.split(',').filter(item => item?.replace(/(^[ {2}]+|[ {2}]+$)/g, ''));
        } else {
            newValues.keywordList = undefined;
        }
        delete newValues.areas;
        dispatch(setFilter({ ...filter, ...newValues, page: 1 }));
    };

    // 重置
    const onReset = () => {
        form.resetFields();
        dispatch(setFilter({ page: 1, size: 20 }));
    };

    // 刷新
    const handleRefresh = () => {
        getOrganizationsList();
    };

    const onChangePage = (page, size) => {
        dispatch(setFilter({ ...filter, page, size }));
    };

    const onShowSizeChange = (_, size) => {
        dispatch(setFilter({ ...filter, size }));
    };

    // 处理所在地区
    const dealArea = row => {
        if (/^北京|上海|天津|重庆/.test(row.province)) {
            return `${row.city}${row.district}`;
        } else {
            return `${row.province}${row.city}${row.district}`;
        }
    };

    // 准备机构别称
    const prepareOrgAliases = org => {
        if (!org?.aliases) return '';

        try {
            // 解析JSON格式的别称数据
            const aliases = JSON.parse(org.aliases);
            if (!Array.isArray(aliases) || aliases.length === 0) return '';

            // 分离当前别称和曾用名
            const currentAliases = aliases.filter(alias => !alias.former);
            const formerAliases = aliases.filter(alias => alias.former);

            let aliasText = '';

            // 添加当前别称
            if (currentAliases.length > 0) {
                const currentNames = currentAliases.map(alias => alias.name).join('、');
                aliasText += currentNames;
            }

            // 添加曾用名
            if (formerAliases.length > 0) {
                const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
                if (!!aliasText) aliasText += '、';
                aliasText += formerNames;
            }

            return aliasText;
        } catch (error) {
            console.error('解析机构别称失败:', error);
            return '';
        }
    };

    const onCopy = msg => {
        copy(msg);
        message.success('已复制到剪贴板');
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const prepareLogoUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 50,
        },
        {
            title: '名称',
            dataIndex: 'name',
            render: (_, row) => {
                return (
                    <div className="org-info">
                        <div className="org-info-content">
                            <div className="org-name">
                                <span>{row.name}</span>
                                {!!prepareLogoUrl(row.logo) && (
                                    <Popover placement="right" content={<img className="org-list-logo" src={prepareLogoUrl(row.logo)} alt={row.name} />}>
                                        <span className="org-logo-icon">
                                            <i className="bi bi-image"></i>
                                        </span>
                                    </Popover>
                                )}
                            </div>
                            {(row.medOwnership || row.medType || !!prepareMedLevelGrade(row)) && (
                                <div className="org-pops">
                                    {row.medOwnership && <span className="medOwnership">{row.medOwnership}</span>}
                                    {row.medType && <span className="medType">{row.medType}</span>}
                                    {prepareMedLevelGrade(row) && <span className="medLevelGrade">{prepareMedLevelGrade(row)}</span>}
                                </div>
                            )}
                            {!!prepareOrgAliases(row) && (
                                <div className="org-aliases">
                                    <Text type="secondary">{prepareOrgAliases(row)}</Text>
                                </div>
                            )}
                            {!!row.mergedToId && (
                                <div className="org-merge-info">
                                    <Tag color="#f50" className="merge-tag" style={{ padding: 2, fontSize: 10, lineHeight: '1em' }}>
                                        合并至
                                    </Tag>
                                    <Link className="merge-name" onClick={() => viewMergeOrg(row)}>
                                        {row.mergedToName}
                                    </Link>
                                    <Text type="secondary">#{row.mergedToId}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: '机构类型', // 机构类型（orgType）：0未分类，1医疗机构，2政府，3学协会，4科研院校，5企业
            dataIndex: 'orgType',
            width: 100,
            render: (_, row) => <>{orgTypeList.find(item => item.value === row.orgType)?.label || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '所在地',
            dataIndex: 'area',
            width: 160,
            render: (_, row) => <>{row.province ? dealArea(row) : <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '审核状态',
            dataIndex: 'reviewStatus',
            width: 100,
            render: (_, row) => (
                <>
                    {row.reviewStatus === 0 && <Text type="warning">待审核</Text>}
                    {row.reviewStatus === 1 && <Text type="success">已通过</Text>}
                    {row.reviewStatus === 2 && <Text type="danger">已驳回</Text>}
                </>
            ),
        },
        {
            title: '来源',
            dataIndex: 'source',
            width: 180,
            render: (_, row) => (
                <>
                    <div>
                        {row.source === 0 ? (
                            '后台新增'
                        ) : (
                            <Popover
                                content={
                                    <>
                                        <span style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onCopy(row.memcard)}>
                                            {row.memcard}
                                        </span>
                                        <small>（点击复制）</small>
                                    </>
                                }
                            >
                                用户新增
                            </Popover>
                        )}
                    </div>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.createdAt}
                        </Text>
                    </div>
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, row) => (
                <span className="operation-btns">
                    <Tooltip title="查看、审核机构信息">
                        <Button size="small" type="link" onClick={() => handleView(row)}>
                            查看
                        </Button>
                    </Tooltip>
                </span>
            ),
        },
    ];

    const handleMerge = () => {
        navigate('/dc/organization/merge');
    };

    const viewMergeOrg = record => {
        setOpenViewOrgDrawer(true);
        setCurrentRecordId(record.mergedToId);
    };

    // 获取机构列表
    const getOrganizationsList = async () => {
        setLoading(true);
        const res = await mergeListApi({ ...filter, allColumn: 0 }); // allColumn: 0 - 代表boss后台机构检索
        setLoading(false);
        if (res && res.status_code === 200 && res.data) {
            setTableData(res.data.records || []);
            setTotal(res.data.total);
        }
    };

    useEffect(() => {
        const newFilter = { ...filter };
        const code = [];
        if (filter.provinceCode) {
            code[0] = filter.provinceCode;
        }
        if (filter.cityCode) {
            code[1] = filter.cityCode;
        }
        if (filter.districtCode) {
            code[2] = filter.districtCode;
        }
        newFilter.areas = code;

        if (filter.keywordList && Array.isArray(filter.keywordList)) {
            newFilter.keywordList = filter.keywordList.join();
        }

        form.setFieldsValue(newFilter);
        getOrganizationsList();
    }, [filter]);

    useEffect(() => {
        document.title = '合并机构管理-机构库';
    }, []);

    const buttonColLayout = computeButtonColLayout(4);

    return (
        <div className="page-container list-page-container dc-organization-merge-page">
            <PageHeader title="合并机构管理" breadcrumb={[{ label: '数据中心' }, { label: '机构库' }, { label: '合并机构管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="organization-filter-box"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} form={form} onFinish={onFinish} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    {...filterFormItemLayout}
                                    label={
                                        <span className="form-item-label-text">
                                            机构标识
                                            <Tooltip title="机构ID或名称、别称、简称、显示名称的关键字，多个ID或关键字使用英文逗号隔开">
                                                <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                            </Tooltip>
                                        </span>
                                    }
                                    name="keywordList"
                                    style={filterFormItemStyle}
                                >
                                    <Input allowClear placeholder="多个关键词用英文逗号分隔" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="机构类型" name="orgType" style={filterFormItemStyle}>
                                    <Select placeholder="选择机构类型" allowClear mode="multiple">
                                        {orgTypeList
                                            && !!orgTypeList.length
                                            && orgTypeList.map(item => (
                                                <Select.Option key={item.value} value={item.value}>
                                                    {item.label}
                                                </Select.Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="所在地区" name="areas" style={filterFormItemStyle}>
                                    <Cascader placeholder="选择地区" options={regionOptions} changeOnSelect showSearch></Cascader>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item {...filterFormItemLayout} label="审核状态" name="reviewStatus" style={filterFormItemStyle}>
                                    <Select placeholder="选择审核状态" allowClear>
                                        <Select.Option value={0}>待审核</Select.Option>
                                        <Select.Option value={1}>已通过</Select.Option>
                                        <Select.Option value={2}>已驳回</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container dc-organization-merge-table-region">
                <div className="main-toolbar dc-organization-merge-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" danger ghost onClick={handleMerge}>
                                合并机构
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新">
                                <Button type="default" icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} onClick={handleRefresh} aria-label="刷新" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap dc-organization-merge-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        bordered
                        sticky
                        size="medium"
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.size,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onShowSizeChange,
                        }}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>

            <AddOrgDrawer openAddOrgDrawer={openAddOrgDrawer} setOpenAddOrgDrawer={setOpenAddOrgDrawer} id={currentRecordId} getOrganizationsList={getOrganizationsList} setCurrentRecordId={setCurrentRecordId} setOpenViewOrgDrawer={setOpenViewOrgDrawer} />

            <ViewOrgDrawer openViewOrgDrawer={openViewOrgDrawer} setOpenViewOrgDrawer={setOpenViewOrgDrawer} id={currentRecordId} setOpenAddOrgDrawer={setOpenAddOrgDrawer} setCurrentRecordId={setCurrentRecordId} getOrganizationsList={getOrganizationsList} />
        </div>
    );
};

export default OrganizationMergeList;
